import { useState } from 'react'
import { Palette, Check, X, Sun, Moon, Sparkles, Plus, Edit } from 'lucide-react'
import { useTheme } from '../contexts/ThemeContext'
import { useModalClose } from '../hooks/useModalClose'
import ThemeCustomizer from './ThemeCustomizer'
import toast from 'react-hot-toast'

export default function ThemeSelector() {
  const { currentTheme, themes, setTheme, loading } = useTheme()
  const [isOpen, setIsOpen] = useState(false)
  const [changing, setChanging] = useState(false)
  const [showCustomizer, setShowCustomizer] = useState(false)
  const [customizeBaseTheme, setCustomizeBaseTheme] = useState<any>(null)
  const { handleBackdropClick } = useModalClose(() => setIsOpen(false))

  const handleThemeChange = async (themeId: number) => {
    try {
      setChanging(true)
      await setTheme(themeId)
      toast.success('Theme applied!')
      setIsOpen(false)
    } catch (error) {
      toast.error('Failed to apply theme')
    } finally {
      setChanging(false)
    }
  }

  if (loading) {
    return null
  }

  return (
    <>
      {/* Theme Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-2 hover:bg-gray-100 rounded-lg transition"
        title="Change Theme"
      >
        <Palette size={20} />
      </button>

      {/* Theme Selector Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4" onClick={handleBackdropClick}>
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <Palette className="w-6 h-6 text-gray-700" />
                <h2 className="text-2xl font-bold text-gray-900">Choose Your Theme</h2>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X size={24} />
              </button>
            </div>

            {/* Create Custom Theme Button */}
            <div className="mb-6">
              <button
                onClick={() => {
                  setCustomizeBaseTheme(null)
                  setShowCustomizer(true)
                }}
                className="w-full p-4 border-2 border-dashed border-gray-300 rounded-xl hover:border-primary-500 hover:bg-gray-50 transition-all flex items-center justify-center gap-2 text-gray-600 hover:text-primary-600"
              >
                <Plus size={20} />
                <span className="font-semibold">Create Custom Theme</span>
              </button>
            </div>

            {/* Theme Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {themes.map((theme) => (
                <div
                  key={theme.id}
                  className={`relative group border-2 rounded-xl transition-all ${
                    currentTheme?.id === theme.id
                      ? 'border-primary-600 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                  style={{
                    backgroundColor: theme.surface_color,
                    color: theme.text_primary,
                  }}
                >
                  <button
                    onClick={() => handleThemeChange(theme.id)}
                    disabled={changing}
                    className="w-full p-4 text-left"
                  >
                    {/* Theme Preview */}
                    <div className="space-y-2 mb-3">
                      <div className="flex gap-1">
                        <div
                          className="w-8 h-8 rounded-lg"
                          style={{ backgroundColor: theme.primary_color }}
                        />
                        <div
                          className="w-8 h-8 rounded-lg"
                          style={{ backgroundColor: theme.secondary_color }}
                        />
                        <div
                          className="w-8 h-8 rounded-lg"
                          style={{ backgroundColor: theme.accent_color }}
                        />
                      </div>
                      <div
                        className="h-16 rounded-lg border"
                        style={{
                          backgroundColor: theme.widget_background,
                          borderColor: theme.widget_border,
                        }}
                      >
                        <div className="p-2 space-y-1">
                          <div
                            className="h-2 rounded"
                            style={{
                              backgroundColor: theme.text_primary,
                              opacity: 0.3,
                            }}
                          />
                          <div
                            className="h-2 rounded w-3/4"
                            style={{
                              backgroundColor: theme.text_secondary,
                              opacity: 0.3,
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Theme Name & Icon */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {theme.is_dark ? (
                          <Moon size={14} style={{ color: theme.text_secondary }} />
                        ) : (
                          <Sun size={14} style={{ color: theme.text_secondary }} />
                        )}
                        <span className="text-sm font-semibold">{theme.name}</span>
                      </div>
                      {theme.is_system && (
                        <Sparkles size={12} style={{ color: theme.accent_color }} />
                      )}
                    </div>

                    {/* Selected Indicator */}
                    {currentTheme?.id === theme.id && (
                      <div
                        className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: theme.primary_color }}
                      >
                        <Check size={16} className="text-white" />
                      </div>
                    )}
                  </button>

                  {/* Customize Button (appears on hover) */}
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setCustomizeBaseTheme(theme)
                      setShowCustomizer(true)
                    }}
                    className="absolute bottom-2 right-2 p-2 bg-white/90 hover:bg-white rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Customize this theme"
                  >
                    <Edit size={14} className="text-gray-700" />
                  </button>
                </div>
              ))}
            </div>

            {/* Info Footer */}
            <div className="mt-6 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <strong>Tip:</strong> Click any theme to apply it instantly. Hover over a theme
                and click the <Edit size={12} className="inline" /> icon to customize it with
                your own colors and fonts!
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Theme Customizer Modal */}
      {showCustomizer && (
        <ThemeCustomizer
          baseTheme={customizeBaseTheme}
          onClose={() => {
            setShowCustomizer(false)
            setCustomizeBaseTheme(null)
          }}
          onSave={() => {
            setShowCustomizer(false)
            setCustomizeBaseTheme(null)
            setIsOpen(false)
          }}
        />
      )}
    </>
  )
}

